import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, CheckCircle, AlertCircle, Loader2, Files } from 'lucide-react';
import { useClientTermsStatus } from '@/hooks/clients/useClientTermsStatus';
import ClientTermsAcceptanceModal from './ClientTermsAcceptanceModal';
import { formatLocalizedDate } from '@/utils/dateHelpers';
import { useIsMobile } from '@/hooks/use-mobile';

interface ClientTermsAcceptanceButtonProps {
  clientId: string;
  clientName: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    birthDate?: string;
    weightKg?: number;
    heightCm?: number;
  };
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

const ClientTermsAcceptanceButton: React.FC<ClientTermsAcceptanceButtonProps> = ({
  clientId,
  clientName,
  client,
  variant = 'outline',
  size = 'sm'
}) => {
  const { t, i18n } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const isMobile = useIsMobile();
  
  const { 
    data: termsStatus, 
    isLoading, 
    error 
  } = useClientTermsStatus(clientId);

  // Always render if there's a terms file, even if there's an error
  if (isLoading) {
    return (
      <Button variant={variant} size={size} disabled>
        <Loader2 className="h-4 w-4 animate-spin" />
        {!isMobile && <span className="ml-2">{t('common.loading')}</span>}
      </Button>
    );
  }

  // Show a default button even if no terms file or error
  if (error || !termsStatus?.currentTermsUrl) {
    return (
      <Button variant="outline" size={size} disabled>
        <FileText className="h-4 w-4" />
        {!isMobile && <span className="ml-2">{t('clients.termsAcceptance.signTerms')}</span>}
      </Button>
    );
  }

  const getButtonContent = () => {
    if (termsStatus.hasAccepted && !termsStatus.needsUpdate) {
      return {
        icon: <Files className="h-4 w-4" />,
        text: t('clients.termsAcceptance.signTerms'),
        variant: 'secondary' as const,
        badge: null
      };
    }

    if (termsStatus.hasAccepted && termsStatus.needsUpdate) {
      return {
        icon: <AlertCircle className="h-4 w-4" />,
        text: t('clients.termsAcceptance.termsNeedUpdate'),
        variant: 'outline' as const,
        badge: null
      };
    }

    return {
      icon: <FileText className="h-4 w-4" />,
      text: t('clients.termsAcceptance.signTerms'),
      variant: variant,
      badge: null
    };
  };

  const buttonContent = getButtonContent();

  return (
    <>
      <div className="flex items-center">
        <Button
          variant={buttonContent.variant}
          size={size}
          onClick={() => setModalOpen(true)}
          className="flex items-center"
        >
          {buttonContent.icon}
          {!isMobile && <span className="ml-2">{buttonContent.text}</span>}
        </Button>
        {!isMobile && buttonContent.badge}
      </div>

      {termsStatus?.currentTermsUrl && (
        <ClientTermsAcceptanceModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          clientId={clientId}
          clientName={clientName}
          client={client}
          termsUrl={termsStatus.currentTermsUrl}
        />
      )}
    </>
  );
};

export default ClientTermsAcceptanceButton;